import React, { useState } from "react";
// Import useParams to handle the /:catId/:templateId from your App.js route
import { useParams } from "react-router-dom"; 
import { Eye, Download, ArrowLeft, Send, Plus, FileText, Calendar, BadgeCheck, Mail, User } from "lucide-react";

// Renamed to OfferLetter to match your App.js import perfectly
export default function OfferLetter() {
  // These values come from your route: letters/editor/:catId/:templateId
  const { catId, templateId } = useParams(); 
  
  const [view, setView] = useState("list");
  const [formData, setFormData] = useState({});
  const [offers, setOffers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sentOffers") || "[]");
    } catch {
      return [];
    }
  });

  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const handleOfferSent = (data) => {
    const newOffer = {
      id: Date.now(),
      ...data,
      status: "Sent",
      sentOn: new Date().toLocaleDateString(),
    };

    const updated = [newOffer, ...offers];
    setOffers(updated);
    localStorage.setItem("sentOffers", JSON.stringify(updated));
    setView("list");
  };

  return (
    <div className="min-h-screen bg-white p-4 md:p-8" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">
        {/* Debug Info (Optional - remove once working) */}
        {/* <div className="text-[10px] text-slate-300 mb-2">Category: {catId} | Template: {templateId}</div> */}

        {view === "list" && (
          <OfferList 
            offers={offers} 
            onNew={() => setView("new")} 
            poppins={poppinsStyle} 
          />
        )}
        
        {view === "new" && (
          <NewOffer
            onBack={() => setView("list")}
            onPreview={(data) => {
              setFormData(data);
              setView("preview");
            }}
            poppins={poppinsStyle}
          />
        )}

        {view === "preview" && (
          <OfferPreview 
            data={formData} 
            onBack={() => setView("new")} 
            onSend={handleOfferSent} 
            poppins={poppinsStyle}
          />
        )}
      </div>
    </div>
  );
}

/* =======================
   SUB-COMPONENTS (Keep these as they were)
======================= */
function OfferList({ offers, onNew, poppins }) {
  return (
    <div className="animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={poppins}>Offer Letters</h1>
          <p className="text-sm text-slate-500">Manage and track candidate recruitment offers.</p>
        </div>
        <button
          onClick={onNew}
          className="flex items-center gap-2 bg-slate-900 hover:bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
        >
          <Plus size={16} /> Generate New Offer
        </button>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-100">
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Candidate</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Designation</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">CTC</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {offers.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-20 text-center">
                   <div className="flex flex-col items-center opacity-40">
                      <FileText size={48} className="mb-2" />
                      <p className="text-sm">No offer history found</p>
                   </div>
                </td>
              </tr>
            ) : (
              offers.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900">{o.name}</span>
                      <span className="text-xs text-slate-400">{o.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{o.designation}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 font-mono">₹{o.ctc}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wide">
                      {o.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-3">
                      <button className="p-2 text-slate-400 hover:text-teal-600 transition-colors" title="View"><Eye size={16} /></button>
                      <button className="p-2 text-slate-400 hover:text-blue-600 transition-colors" title="Download"><Download size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewOffer({ onBack, onPreview, poppins }) {
  const [data, setData] = useState({
    email: "", name: "", mobile: "", joiningDate: "",
    bond: "", designation: "", experience: "", ctc: "",
    termsText: "",
  });

  return (
    <div className="max-w-4xl mx-auto animate-in slide-in-from-bottom-4 duration-500">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-slate-900 mb-6 transition-colors text-sm">
        <ArrowLeft size={16} /> Back to Dashboard
      </button>

      <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm">
        <div className="mb-8 border-b border-slate-50 pb-6">
           <h2 className="text-xl font-semibold text-slate-900" style={poppins}>Offer Information</h2>
           <p className="text-sm text-slate-500">Enter candidate and compensation details below.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input label="Email Address" icon={<Mail size={14}/>} value={data.email} onChange={(v) => setData({...data, email: v})} />
          <Input label="Full Name" icon={<User size={14}/>} value={data.name} onChange={(v) => setData({...data, name: v})} />
          <Input label="Designation" icon={<BadgeCheck size={14}/>} value={data.designation} onChange={(v) => setData({...data, designation: v})} />
          <Input label="Joining Date" type="date" icon={<Calendar size={14}/>} value={data.joiningDate} onChange={(v) => setData({...data, joiningDate: v})} />
          <Input label="Annual CTC" value={data.ctc} onChange={(v) => setData({...data, ctc: v})} />
          <Select label="Experience Level" value={data.experience} onChange={(v) => setData({...data, experience: v})} />
        </div>

        <div className="mt-8">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Terms & Special Clauses</label>
          <textarea
            className="w-full border border-slate-100 rounded-xl px-4 py-3 h-32 bg-slate-50/50 focus:ring-1 focus:ring-teal-500 focus:bg-white outline-none transition-all text-sm"
            placeholder="Outline specific terms, notice periods, or confidentiality clauses..."
            value={data.termsText}
            onChange={(e) => setData({...data, termsText: e.target.value})}
          />
        </div>

        <div className="mt-10 flex justify-end gap-3 border-t border-slate-50 pt-6">
          <button onClick={onBack} className="px-6 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">Cancel</button>
          <button
            onClick={() => onPreview(data)}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-all shadow-md flex items-center gap-2"
          >
            Preview Letter <Eye size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferPreview({ data, onBack, onSend, poppins }) {
  return (
    <div className="max-w-3xl mx-auto animate-in zoom-in-95 duration-300">
      <div className="bg-white border border-slate-200 rounded-lg p-12 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-teal-500"></div>
        <div className="flex justify-between items-start mb-12">
           <div className="uppercase font-bold tracking-tighter text-xl">Company<span className="text-teal-500">Logo</span></div>
           <div className="text-right text-[10px] text-slate-400 leading-tight">
             OFFICIAL DOCUMENT<br/>REF: {Date.now().toString().slice(-6)}
           </div>
        </div>

        <div className="space-y-6 text-slate-800 leading-relaxed text-sm">
          <p className="font-medium">Date: {new Date().toLocaleDateString()}</p>
          <p>Dear <span className="font-bold">{data.name}</span>,</p>
          <p>Further to our discussions, we are pleased to offer you the position of <span className="font-bold text-teal-700">{data.designation}</span>.</p>
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-2">
             <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Annual CTC:</span>
                <span className="font-bold">₹{data.ctc}</span>
             </div>
             <div className="flex justify-between border-b border-slate-200 pb-2">
                <span className="text-slate-500">Joining Date:</span>
                <span className="font-bold">{data.joiningDate}</span>
             </div>
          </div>
          <p className="text-xs text-slate-600 italic">{data.termsText || "Standard company employment terms apply."}</p>
        </div>
        <div className="mt-16 pt-8 border-t border-slate-100">
           <div className="w-32 h-12 border-b border-slate-300 mb-2"></div>
           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Authorized Signatory</p>
        </div>
      </div>
      <div className="mt-8 flex justify-center gap-4">
        <button onClick={onBack} className="px-8 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all">Edit Details</button>
        <button onClick={() => onSend(data)} className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-2 rounded-lg text-sm font-medium transition-all shadow-lg flex items-center gap-2">
          Confirm & Send Email <Send size={16} />
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", icon }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <div className="relative flex items-center">
        <div className="absolute left-3 text-slate-400">{icon}</div>
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
          className={`w-full border border-slate-100 rounded-lg ${icon ? 'pl-9' : 'px-4'} py-2 bg-slate-50/50 focus:ring-1 focus:ring-teal-500 focus:bg-white outline-none transition-all text-sm`}
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      </div>
    </div>
  );
}

function Select({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-100 rounded-lg px-4 py-2 bg-slate-50/50 focus:ring-1 focus:ring-teal-500 focus:bg-white outline-none transition-all text-sm"
      >
        <option value="">Select Level</option>
        <option>Fresher (Junior)</option>
        <option>Experienced (Senior)</option>
        <option>Leadership (Lead/Manager)</option>
      </select>
    </div>
  );
}